/**
 * „Vor dem nächsten Monat“: Was ihr in diesem Zug noch tun könntet, bevor ihr auf „Weiter“ klickt.
 * Reine Oberfläche, gilt für alle Partien. Jeder Eintrag führt mit einem Klick an die richtige Stelle.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useGame, useS } from '../../store/game';
import { usePref } from '../../lib/pref';
import { Panel } from '../../components/ui';
import { issuesNow } from '../../engine/axes';
import { busyOf, ownsLaw, issueDraftable } from '../../engine/coalition';
import { capOf } from '../../engine/adm';
import { COM_MAX } from '../../engine/com';
import { DEAL_COST } from '../../engine/parliament';
import { lawOn } from '../../engine/campaign';
import { de } from '../../engine/util';
import { MONTHS } from '../../content/consts';
import { useUnlocked } from './unlock';

type Item = { k: string; t: ReactNode; act?: ReactNode; tone?: 'up' | 'warn' };

/** Einträge der Liste, null wenn sie gerade nicht gilt. */
function useTodoItems(): Item[] | null {
  const S: any = useS(); const { dispatch } = useGame(); const un = useUnlocked();
  if (!S || S.mp || S.phase !== 'govern' || S.editing) return null;
  const items: Item[] = [];
  const mine = (S.laws || []).filter((L: any) => ownsLaw('YOU', L) && !L.bestand);
  for (const L of mine.filter((x: any) => x.stage === 'ready'))
    items.push({ k: 'r' + L.id, tone: 'up', t: <>„{L.t}“ ist fertig und kann ins Kabinett.</>, act: <button type="button" className="small yes" onClick={() => dispatch('cabinet', L.id)}>Einbringen</button> });
  for (const L of mine.filter((x: any) => x.stage === 'failed' || x.stage === 'blocked'))
    items.push({ k: 'f' + L.id, tone: 'warn', t: <>„{L.t}“ ist gescheitert. Überarbeiten oder zu den Akten legen.</>, act: <><button type="button" className="small" onClick={() => dispatch('edit', L.id)}>Überarbeiten</button><button type="button" className="small" onClick={() => dispatch('drop', L.id)}>Ablegen</button></> });
  for (const L of mine.filter((x: any) => x.stage === 'vm' && !x.deal))
    items.push({ k: 'v' + L.id, t: <>„{L.t}“ hängt im Vermittlungsausschuss. Ein Angebot an die Länder ({de(DEAL_COST, 1)} Mrd € im Jahr) macht eine Einigung wahrscheinlicher.</>, act: <button type="button" className="small" onClick={() => dispatch('brDeal', L.id)}>Angebot machen</button> });
  const free = capOf('YOU') - busyOf('YOU');
  const open1 = issuesNow().filter((i: any) => S.issues[i.id]?.on && !S.issues[i.id].law && issueDraftable('YOU', i.id)).sort((a: any, b: any) => S.issues[b.id].sev - S.issues[a.id].sev);
  // Wahlversprechen, die noch offen sind
  const rel = S.m - S.m0;
  const prom = (S.promises || []).filter((p: any) => p.leg === S.leg && p.state === 'open' && p.k === 'iss' && !lawOn(p.iss, S.leg) && S.issues[p.iss] && !S.issues[p.iss].law);
  if (free > 0) {
    for (const p of prom.slice(0, 2)) items.push({ k: 'p' + p.iss, tone: rel >= MONTHS - 12 ? 'warn' : undefined, t: <>Wahlversprechen offen: {p.t}.</>, act: <button type="button" className="small yes" onClick={() => dispatch('draft', p.iss)}>Entwerfen</button> });
    const rest = open1.filter((i: any) => !prom.some((p: any) => p.iss === i.id)).slice(0, 2);
    if (rest.length) items.push({ k: 'cap', t: <>Eure Ministerien können noch {free === 1 ? 'ein Gesetz' : `${free} Gesetze`} übernehmen. Am dringendsten: {rest.map((i: any) => `${i.n} (${Math.round(S.issues[i.id].sev)})`).join(', ')}.</>,
      act: <>{rest.map((i: any) => <button type="button" key={i.id} className="small" onClick={() => dispatch('draft', i.id)}>{i.n}</button>)}</> });
  }
  for (const E of (S.evals || []).filter((e: any) => e.k !== 'erfolg' && e.fixed == null && !S.issues[e.iss]?.law).slice(-2))
    items.push({ k: 'e' + E.lid, tone: 'warn', t: <>Die Evaluierung empfiehlt, „{E.t}“ nachzubessern.</>, act: <button type="button" className="small" onClick={() => dispatch('evalFix' as any, E.lid)}>Nachbessern</button> });
  if (un('presse') && S.com && (S.com.pts ?? 0) >= COM_MAX)
    items.push({ k: 'com', t: <>Alle {COM_MAX} Termine für Öffentlichkeitsarbeit sind frei. Nächsten Monat verfällt einer.</>, act: <button type="button" className="small" onClick={() => dispatch('tab', 'presse')}>Zur Kommunikation</button> });
  if ((S.lapsed || []).length && S.tab !== 'lage')
    items.push({ k: 'lap', t: <>{S.lapsed.length} Entwürfe aus der letzten Legislatur lassen sich wieder aufnehmen.</>, act: <button type="button" className="small" onClick={() => dispatch('tab', 'lage')}>Ansehen</button> });
  return items;
}
const TodoList = ({ items }: { items: Item[] }) => items.length
  ? <ul className="todolist">{items.map((x) => <li key={x.k} className={x.tone || ''}><span>{x.t}</span>{x.act && <div className="todoacts">{x.act}</div>}</li>)}</ul>
  : <p className="sub">Nichts Dringendes. Mit „Weiter“ läuft die Zeit bis zum nächsten wichtigen Ereignis.</p>;

/** Panel in der rechten Spalte (auf schmalen Bildschirmen ausgeblendet, dort gibt es TodoBar). */
export function Todo() {
  const items = useTodoItems();
  const [open, setOpen] = usePref('kabinett-todo-offen', true);
  if (!items) return null;
  const title = <button type="button" className="ptoggle" aria-expanded={open} onClick={() => setOpen(!open)}>Vor dem nächsten Monat</button>;
  if (!open) return <Panel title={title} aside={items.length ? `${items.length} offen` : 'alles erledigt'} className="todo closed"><span className="sub">{items.length ? 'Es gibt noch etwas zu tun.' : 'Nichts Dringendes.'}</span></Panel>;
  return (
    <Panel title={title} aside={items.length ? `${items.length} offen` : 'alles erledigt'} className="todo">
      <TodoList items={items} />
    </Panel>
  );
}

/** Schmale Bildschirme: Knopf „Zu tun“ in der Leiste unten, öffnet die Liste als Blatt von unten. */
export function TodoBar() {
  const items = useTodoItems(); const [open, setOpen] = useState(false);
  useEffect(() => { if (!open) return; const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); }; addEventListener('keydown', k); return () => removeEventListener('keydown', k); }, [open]);
  const on = !!items; useEffect(() => { if (!on) setOpen(false); }, [on]);
  if (!items) return null;
  const n = items.length;
  return (
    <>
      <button type="button" className={'todobtn' + (n ? ' has' : '')} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>Zu tun <b>{n}</b></button>
      {open && <>
        <div className="todoshade" onClick={() => setOpen(false)} />
        <div className="todosheet" role="dialog" aria-modal="true" aria-label="Vor dem nächsten Monat" onClick={(e) => { if ((e.target as HTMLElement).closest('.todoacts button')) setOpen(false); }}>
          <div className="todosheethead"><span className="label">Vor dem nächsten Monat · {n ? `${n} offen` : 'alles erledigt'}</span><button type="button" className="small" onClick={() => setOpen(false)}>Schließen</button></div>
          <TodoList items={items} />
        </div>
      </>}
    </>
  );
}
